import React, { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  Grid,
  Typography,
  makeStyles,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import { DefaultButton, colors, Footer } from 'design-system';
import { Quotation } from 'data-layer-cr/dist/models';
import { NATURAL_PERSON } from 'data-layer-cr/dist/constants/customerTypes';
import {
  validLegalEntity,
  customerWithoutNulls,
  appliesAutoPxDiscount,
} from 'data-layer-cr/dist/functions/quotations';

// Redux
import {
  login,
  setUserProperties,
  CUSTOMER_PHONE,
  WELCOME_PAGE_STEP,
  START_DATE,
  trackEvent,
  CURRENT_STEP,
} from 'src/analytics';
import { RootState } from '../../../store/rootReducer';

const useStyles = makeStyles({
  gradientBackground: {
    height: '100%',
    minHeight: 'calc(100vh - 56px)',
    background: 'linear-gradient(180deg, #CEE8FF 10.19%, #FFFFFF 100%)',
  },
  container: {
    padding: 32,
  },
  buttonContainer: {
    width: 240,
    display: 'inline-block',
    marginTop: -50,
  },
  actionButton: {
    marginTop: '10px',
  },
  helloText: {
    fontWeight: 'bold',
    fontSize: '19px',
    marginTop: '10px',
  },
  introText: {
    fontWeight: 'bold',
    fontSize: '17px',
    textAlign: 'center',
    lineHeight: '19.98px',
  },
  autoPxLogo: {
    height: '15px',
    alignSelf: 'center',
  },
  centerText: {
    fontWeight: 'normal',
    fontSize: '14px',
    lineHeight: '20px',
    color: colors.madison,
  },
  checkArt: {
    marginLeft: '1.5rem',
    marginRight: '1rem',
    height: '-moz-available',
    alignSelf: 'center',
  },
  lineArt: {
    transform: 'rotate(90deg)',
    borderBottom: '1px solid #A3A2A2',
    alignSelf: 'center',
    width: '30px',
  },
});

const useTextCheck = (text: string): JSX.Element => {
  const classes = useStyles();
  return (
    <Grid item xs={12} style={{ display: 'flex' }}>
      <img
        src="/images/check-mark.svg"
        alt="check-mark"
        className={classes.checkArt}
      />
      <Typography variant="body2" className={classes.centerText}>
        {text}
      </Typography>
    </Grid>
  );
};

export const InspectionWelcome = ({
  quotation,
  onStartRequested,
}: Props): JSX.Element => {
  const classes = useStyles();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const customer = useMemo(() => {
    return quotation.customerType === NATURAL_PERSON
      ? {
          name: customerWithoutNulls(quotation).firstName,
          email: customerWithoutNulls(quotation).email,
          phone: customerWithoutNulls(quotation).phone,
          lastName: customerWithoutNulls(quotation).lastName,
        }
      : {
          name: validLegalEntity(quotation).name,
          email: validLegalEntity(quotation).email,
          phone: validLegalEntity(quotation).phone,
          lastName: '',
        };
  }, [quotation]);

  const handleStartRequest = (): void => {
    login(quotation.id.toString());
    setUserProperties({
      $distinct_id: quotation.id.toString(),
      ID: quotation.id.toString(),
      $name: `${customer.name} ${customer.lastName}`,
      $email: customer.email,
      [CUSTOMER_PHONE]: customer.phone,
      [CURRENT_STEP]: WELCOME_PAGE_STEP,
    });
    trackEvent({
      eventName: WELCOME_PAGE_STEP,
      eventProperties: {
        [START_DATE]: new Date().toLocaleString(),
      },
    });
    onStartRequested();
  };

  return (
    <div className={classes.gradientBackground}>
      <div className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12} style={{ display: 'flex' }}>
            <img src="/images/assa_logo_blue.svg" alt="logo" />
            {appliesAutoPxDiscount(quotation) && (
              <>
                <div className={classes.lineArt} />
                <img
                  src="/images/AutoPx.svg"
                  alt="logo-auto-px"
                  data-testid="logo-auto-px"
                  className={classes.autoPxLogo}
                />
              </>
            )}
          </Grid>
          <Grid xs={12} item container justifyContent="center">
            <Typography className={classes.helloText} variant="body1">
              ¡Hola! {customer.name}
            </Typography>
          </Grid>
          <Grid item xs={12} container justifyContent="center">
            <Typography variant="h3" className={classes.introText}>
              Inspeccionar desde tu celular es fácil y rápido.
            </Typography>
          </Grid>
          <Grid item xs={12} container justifyContent="center">
            {quotation.customerType === NATURAL_PERSON ? (
              <img src="/images/inspection_welcome.svg" alt="Welcome" />
            ) : (
              <img src="/images/inspection_comp.svg" alt="Welcome-company" />
            )}
          </Grid>
          {useTextCheck('Debes estar cerca del auto.')}
          {useTextCheck('Ten a mano tu registro vehicular.')}
          <Grid
            item
            xs={12}
            container
            justifyContent="center"
            style={{ marginTop: '10px' }}
          >
            <DefaultButton
              id="sales-web-inspection-button-startinspection"
              data-testid="start-button"
              fullWidth
              onClick={handleStartRequest}
            >
              ¡Comencemos!
            </DefaultButton>
          </Grid>
        </Grid>
      </div>
      <Footer
        IsMobile={isMobile}
        country="Costa Rica"
        linkText="Consulte nuestros términos y condiciones"
        linkHref="https://www.assanet.cr/condiciones-generales/#seguros-generales"
      />
    </div>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
} => ({
  quotation: state.quotation.quotation,
});

const connector = connect(mapStateToProps, {});
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onStartRequested: () => void;
}

export default connector(InspectionWelcome);
